import { getRealtimeDb } from '../../../lib/realtimeDb.js';
import { readGroupState } from './state.js';

/**
 * Firebase Realtime Database group-sync adapter — the real multi-device path.
 *
 * Session state lives at `sessions/{code}` and every participant subscribes to
 * the whole node, so a vote on one phone appears on all the others within a
 * round trip.
 *
 * @param {string} code
 * @returns {Promise<import('./index.js').GroupSync | null>} `null` when Firebase isn't configured.
 */
export async function createFirebaseSync(code) {
  const db = await getRealtimeDb();
  if (!db) return null;

  const { ref, get, onValue, set, remove, serverTimestamp } = await import('firebase/database');
  const sessionRef = ref(db, `sessions/${code}`);

  return {
    code,
    isRealtime: true,

    async snapshot() {
      const value = (await get(sessionRef)).val();
      return value ? readGroupState(value) : null;
    },

    async join({ memberId, name }) {
      await set(ref(db, `sessions/${code}/members/${memberId}`), {
        name,
        joinedAt: serverTimestamp(),
      });
    },

    async invite(guests) {
      /** @type {Record<string, {name: string}>} */
      const roster = {};
      for (const guest of guests) roster[guest.id] = { name: guest.name };
      await set(ref(db, `sessions/${code}/invites`), roster);
    },

    async setDeck(selection) {
      await set(ref(db, `sessions/${code}/deck`), selection);
    },

    subscribe(onState) {
      return onValue(sessionRef, (snapshot) => {
        onState(readGroupState(snapshot.val()));
      });
    },

    async recordVote({ memberId, restaurantId, direction, time, place }) {
      await set(ref(db, `sessions/${code}/votes/${memberId}/${restaurantId}`), direction);
      if (time) {
        await set(ref(db, `sessions/${code}/times/${memberId}/${restaurantId}`), time);
      }
      if (place && direction === 'right') {
        await set(ref(db, `sessions/${code}/places/${restaurantId}`), place);
      }
    },

    async submit({ memberId }) {
      await set(ref(db, `sessions/${code}/submitted/${memberId}`), serverTimestamp());
    },

    async end({ memberId }) {
      await set(ref(db, `sessions/${code}/ended`), { at: serverTimestamp(), by: memberId });
    },

    async leave({ memberId }) {
      // Remove only this participant. The session survives for everyone else,
      // which is why the whole node is not deleted here.
      await remove(ref(db, `sessions/${code}/members/${memberId}`));
      await remove(ref(db, `sessions/${code}/votes/${memberId}`));
      await remove(ref(db, `sessions/${code}/times/${memberId}`));
      await remove(ref(db, `sessions/${code}/submitted/${memberId}`));
    },
  };
}
